import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/config";
import PageShell from "../layout/PageShell";

type EnvironmentName = "DEV" | "ESSAI" | "PROD";

type CsrForm = {
  userId: string;
  authCode: string;
  qstNumber: string;
  gstNumber: string;
  dossierNumber: string;
  timezone: string;
  activitySubsector: string;
  nickname: string;
};

type BusinessForm = {
  Name: string;
  email: string;
  phone: string;
  address_1: string;
  address_2: string;
  city: string;
  Province: string;
  postcode: string;
  country: string;
};

type HeaderForm = {
  idsev: string;
  idversi: string;
  codcertif: string;
  idpartn: string;
  versi: string;
  versiparn: string;
};

type ServerLog = { id: string; at: string; label: string; text: string; isError?: boolean };

type ServerCertificateStatus = {
  envirn: EnvironmentName;
  enrolled: boolean;
  organization: string;
  caseEssai: string;
  replaceCase: string;
  deleteCase: string;
  apprlinit: string;
  business: BusinessForm;
  csrForm: CsrForm;
  headers: HeaderForm;
  cert: { name: string; nickname: string; serial: string; expiry: string; fullText: string };
  csrPem: string;
  csrGeneratedAt: string;
  csrUsedAt: string;
  csrUsedFor: string;
  hasRequest: boolean;
  csrFormSaved: boolean;
  logs: ServerLog[];
  nicknamesByCase?: Record<string, string>;
};

const emptyBusiness: BusinessForm = {
  Name: "",
  email: "",
  phone: "",
  address_1: "",
  address_2: "",
  city: "",
  Province: "",
  postcode: "",
  country: "",
};

const testCsr: CsrForm = {
  userId: "3601837200",
  authCode: "B8T8-W8W8",
  qstNumber: "3601837200TQ0001",
  gstNumber: "3601837200RT0001",
  dossierNumber: "ER0001",
  timezone: "-05:00",
  activitySubsector: "Restaurant",
  nickname: "Certificat du serveur",
};

const productionCsr: CsrForm = {
  userId: "",
  authCode: "",
  qstNumber: "",
  gstNumber: "",
  dossierNumber: "",
  timezone: "-05:00",
  activitySubsector: "Restaurant",
  nickname: "Certificat du serveur",
};

function csrDefaults(envirn: EnvironmentName): CsrForm {
  return envirn === "PROD" ? productionCsr : testCsr;
}

const SERVER_ENV_KEY = "serverCertificateEnvirn";

function readRememberedServerEnv(): EnvironmentName {
  try {
    const saved = localStorage.getItem(SERVER_ENV_KEY);
    if (saved === "DEV" || saved === "ESSAI" || saved === "PROD") return saved;
  } catch {
    // The page opens on DEV only when no environment was saved.
  }
  return "DEV";
}

function sanitizeCertificateNickname(value: string) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function savedServerNickname(names: Record<string, string>, caseId: string, profileName?: string) {
  const forCase = String(names[caseId] || "").trim();
  const name = forCase || String(profileName || "").trim();
  return sanitizeCertificateNickname(name);
}

const emptyHeaders: HeaderForm = {
  idsev: "",
  idversi: "",
  codcertif: "",
  idpartn: "",
  versi: "",
  versiparn: "",
};

const emptyStatus: ServerCertificateStatus = {
  envirn: "DEV",
  enrolled: false,
  organization: "",
  caseEssai: "500.001",
  replaceCase: "500.002",
  deleteCase: "500.003",
  apprlinit: "SRV",
  business: emptyBusiness,
  csrForm: testCsr,
  csrFormSaved: false,
  headers: emptyHeaders,
  cert: { name: "", nickname: "", serial: "", expiry: "", fullText: "" },
  csrPem: "",
  csrGeneratedAt: "",
  csrUsedAt: "",
  csrUsedFor: "",
  hasRequest: false,
  logs: [],
};

function serverCertificateTitle(enrolled: boolean, serial: string, expiry: string) {
  if (!enrolled) return "Server certificate";
  const last4 = String(serial || "").replace(/[^0-9A-Fa-f]/g, "").slice(-4).toUpperCase();
  const expires = new Date(expiry);
  const daysLeft = !expiry || Number.isNaN(expires.getTime())
    ? null
    : Math.floor((expires.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  const days = daysLeft == null ? "" : `${daysLeft} ${Math.abs(daysLeft) === 1 ? "day remaining" : "days remaining"}`;
  const parts = ["Server certificate", last4, days].filter(Boolean);
  const title = parts.join(" · ");
  if (daysLeft != null && daysLeft >= 0 && daysLeft <= 927) {
    return `${title}. Your certificate is 2.5 years old. Please change it.`;
  }
  return title;
}

function expiryStatus(expiry: string) {
  const expires = new Date(expiry);
  if (!expiry || Number.isNaN(expires.getTime())) return null;
  const daysLeft = Math.floor((expires.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  if (daysLeft < 0) {
    return { daysLeft, label: "Expired", warning: true, notice: "This certificate has expired. Replace it." };
  }
  const label = `${daysLeft} ${daysLeft === 1 ? "day remaining" : "days remaining"}`;
  if (daysLeft < 90) {
    return {
      daysLeft,
      label,
      warning: true,
      notice: "Fewer than 90 days remain before this certificate expires. Replace it.",
    };
  }
  return { daysLeft, label, warning: false, notice: "" };
}

function CaseEssaiField({
  value,
  onChange,
  readOnly = false,
  onEditingChange,
}: {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  onEditingChange?: (editing: boolean) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const notify = useRef(onEditingChange);
  notify.current = onEditingChange;
  const valid = /^\d{3}\.\d\d{2}$/.test(draft.trim());

  useEffect(() => {
    setDraft(value);
    setEditing(false);
    notify.current?.(false);
  }, [value]);

  return (
    <FormControl>
      <FormLabel>CASESSAI</FormLabel>
      <InputGroup>
        <Input
          value={editing ? draft : value}
          isReadOnly={readOnly || !editing}
          pr={readOnly ? undefined : "5.5rem"}
          onChange={(event) => setDraft(event.target.value)}
        />
        {readOnly ? null : (
          <InputRightElement width="5.5rem">
            <Button
              size="sm"
              h="1.75rem"
              variant={editing ? "solid" : "outline"}
              colorScheme={editing ? "blue" : undefined}
              onClick={() => {
                if (!editing) {
                  setDraft(value);
                  setEditing(true);
                  notify.current?.(true);
                  return;
                }
                if (!valid) return;
                onChange(draft.trim());
                setEditing(false);
                notify.current?.(false);
              }}
            >
              {editing ? "Save" : "Edit"}
            </Button>
          </InputRightElement>
        )}
      </InputGroup>
      {editing && !valid ? (
        <Text mt={1} fontSize="sm" color="red.600">Enter a test case as 000.000.</Text>
      ) : null}
    </FormControl>
  );
}

function StepNote({ children }: { children: React.ReactNode }) {
  return (
    <Box mb={4} bg="#f4f8fa" border="1px solid" borderColor="#d5e4ea" borderRadius="md" px={4} py={3}>
      <Text fontSize="sm" color="gray.700" whiteSpace="pre-line">{children}</Text>
    </Box>
  );
}

function callableError(error: unknown, fallback: string) {
  const err = error as { message?: string };
  const message = String(err?.message || "").replace(/^FirebaseError:\s*/i, "").trim();
  return message || fallback;
}

const ServerCertificatePage: React.FC = () => {
  const toast = useToast();
  const [envirn, setEnvirn] = useState<EnvironmentName>(readRememberedServerEnv);
  const [status, setStatus] = useState<ServerCertificateStatus>(emptyStatus);
  const [business, setBusiness] = useState<BusinessForm>(emptyBusiness);
  const [csrForm, setCsrForm] = useState<CsrForm>(testCsr);
  const [savedCsrForm, setSavedCsrForm] = useState<CsrForm>(testCsr);
  const [headers, setHeaders] = useState<HeaderForm>(emptyHeaders);
  const [serial, setSerial] = useState("");
  const [addCase, setAddCase] = useState("500.001");
  const [replaceCase, setReplaceCase] = useState("500.002");
  const [deleteCase, setDeleteCase] = useState("500.003");
  const [nicknamesByCase, setNicknamesByCase] = useState<Record<string, string>>({});
  const [nicknameEditing, setNicknameEditing] = useState(false);
  const [nicknameDraft, setNicknameDraft] = useState("");
  const [addCaseEditing, setAddCaseEditing] = useState(false);
  const [replaceCaseEditing, setReplaceCaseEditing] = useState(false);
  const [deleteCaseEditing, setDeleteCaseEditing] = useState(false);
  const loadedEnv = useRef<EnvironmentName | "">("");
  const [busy, setBusy] = useState("");
  const [businessLocked, setBusinessLocked] = useState(true);
  const [copied, setCopied] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    typedWord?: string;
    action: () => Promise<void>;
  } | null>(null);
  const [typedConfirm, setTypedConfirm] = useState("");
  const cancelRef = useRef<HTMLButtonElement>(null);

  const apply = useCallback((next: ServerCertificateStatus) => {
    const envNow = (next.envirn || envirn) as EnvironmentName;
    const form = { ...csrDefaults(envNow), ...(next.csrForm || {}) };
    const names = next.nicknamesByCase && typeof next.nicknamesByCase === "object" ? next.nicknamesByCase : {};
    setNicknamesByCase(names);
    setNicknameEditing(false);
    const view = { ...emptyStatus, ...next, envirn: envNow, csrForm: form };
    setStatus(view);
    setBusiness({ ...emptyBusiness, ...(next.business || {}) });
    setCsrForm(form);
    setSavedCsrForm(form);
    setHeaders({ ...emptyHeaders, ...(next.headers || {}) });
    setSerial(next.cert?.serial || "");
    if (loadedEnv.current !== envNow) {
      loadedEnv.current = envNow;
      setAddCase(String(next.caseEssai || (envNow === "PROD" ? "000.000" : "500.001")));
      setReplaceCase(String(next.replaceCase || (envNow === "PROD" ? "000.000" : "500.002")));
      setDeleteCase(String(next.deleteCase || (envNow === "PROD" ? "000.000" : "500.003")));
      setAddCaseEditing(false);
      setReplaceCaseEditing(false);
      setDeleteCaseEditing(false);
    }
  }, [envirn]);

  const loadStatus = useCallback(async (nextEnv: EnvironmentName) => {
    const call = httpsCallable<{ envirn: EnvironmentName }, ServerCertificateStatus>(
      functions,
      "getServerCertificateStatus"
    );
    const result = await call({ envirn: nextEnv });
    apply({ ...emptyStatus, ...result.data, envirn: nextEnv });
  }, [apply]);

  useEffect(() => {
    setNicknameEditing(false);
  }, [addCase, envirn]);

  useEffect(() => {
    try {
      localStorage.setItem(SERVER_ENV_KEY, envirn);
    } catch {
      // The open environment still applies for this visit.
    }
  }, [envirn]);

  useEffect(() => {
    loadStatus(envirn).catch((error) => {
      toast({
        title: "Server certificate",
        description: callableError(error, "The server certificate could not be loaded."),
        status: "error",
      });
    });
  }, [envirn, loadStatus, toast]);

  const run = async (name: string, fn: string, payload: Record<string, unknown>, success: string) => {
    setBusy(name);
    try {
      const call = httpsCallable<Record<string, unknown>, ServerCertificateStatus>(functions, fn);
      const result = await call({ envirn, ...payload });
      apply(result.data);
      toast({ title: success, status: "success" });
    } catch (error) {
      toast({ title: "Server certificate", description: callableError(error, "The request failed."), status: "error" });
    } finally {
      setBusy("");
    }
  };

  const expiry = expiryStatus(status.cert.expiry);
  const requestUsed = Boolean(status.csrUsedAt);
  const usedAction = status.csrUsedFor === "replace" ? "replace the active certificate" : "add the certificate";
  const usedWhen = status.csrUsedAt ? new Date(status.csrUsedAt).toLocaleString() : "";
  const csrChanged = JSON.stringify(csrForm) !== JSON.stringify(savedCsrForm);
  const canAdd = Boolean(status.hasRequest) && !requestUsed;
  const canReplace = Boolean(status.hasRequest) && status.enrolled && !requestUsed;
  const typedWordMatches = !pendingConfirm?.typedWord || typedConfirm === pendingConfirm.typedWord;

  const ask = (next: NonNullable<typeof pendingConfirm>) => {
    setTypedConfirm("");
    setPendingConfirm(next);
  };

  const field = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    readOnly = false
  ) => (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Input value={value} isReadOnly={readOnly} onChange={(event) => onChange(event.target.value)} />
    </FormControl>
  );

  return (
    <PageShell
      title={serverCertificateTitle(status.enrolled, status.cert.serial, status.cert.expiry)}
      description="One certificate for the cloud backend in each environment. The private key stays in Firestore."
      wide
    >
      <Box bg="white" border="1px solid" borderColor="#d5e4ea" borderRadius="lg" p={6}>
        <FormControl maxW="240px" mb={4}>
          <FormLabel>Environment</FormLabel>
          <Select
            value={envirn}
            fontWeight="bold"
            color={envirn === "ESSAI" ? "#dc2626" : envirn === "PROD" ? "#15803d" : "#1d4ed8"}
            onChange={(event) => {
            const next = event.target.value as EnvironmentName;
            const defaults = csrDefaults(next);
            try {
              localStorage.setItem(SERVER_ENV_KEY, next);
            } catch {
              // The selection still applies for this visit.
            }
            setEnvirn(next);
            setCsrForm(defaults);
            setSavedCsrForm(defaults);
            setNicknamesByCase({});
            setNicknameEditing(false);
          }}>
            <option value="DEV" style={{ color: "#1d4ed8", fontWeight: 700 }}>DEV</option>
            <option value="ESSAI" style={{ color: "#dc2626", fontWeight: 700 }}>ESSAI</option>
            <option value="PROD" style={{ color: "#15803d", fontWeight: 700 }}>PROD</option>
          </Select>
        </FormControl>
        <Tabs colorScheme="blue" index={tabIndex} onChange={setTabIndex}>
          <TabList>
            <Tab>Business</Tab>
            <Tab>Active certificate</Tab>
            <Tab>Create CSR</Tab>
            <Tab>Add certificate</Tab>
            <Tab>Replace certificate</Tab>
            <Tab>Delete certificate</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <StepNote>
                These details identify the business for this environment. They are stored in Firestore with the server certificate. They are not sent inside the certificate request. Save them before you generate a request if you want this record to match the business you enroll.
              </StepNote>
              <HStack justify="flex-end" mb={3}>
                {businessLocked ? (
                  <Button variant="outline" onClick={() => setBusinessLocked(false)}>Edit</Button>
                ) : (
                  <Button
                    colorScheme="blue"
                    isLoading={busy === "business"}
                    onClick={() => ask({
                      title: "Save business details?",
                      message: `These details will be stored in Firestore for ${envirn}. They are not sent to Revenu Québec.`,
                      confirmLabel: "Save",
                      action: async () => {
                        await run("business", "saveServerCertificateProfile", { business }, "Business saved");
                        setBusinessLocked(true);
                      },
                    })}
                  >
                    Save
                  </Button>
                )}
              </HStack>
              <Text fontSize="sm" color="gray.600" mb={4}>
                These details are stored with the server certificate for this environment.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {([
                  ["Name", "Company name"],
                  ["email", "Email"],
                  ["phone", "Phone"],
                  ["address_1", "Address line 1"],
                  ["address_2", "Address line 2"],
                  ["city", "City"],
                  ["Province", "Province"],
                  ["postcode", "Postal code"],
                  ["country", "Country"],
                ] as Array<[keyof BusinessForm, string]>).map(([key, label]) => (
                  <FormControl key={key}>
                    <FormLabel>{label}</FormLabel>
                    <Input
                      value={business[key]}
                      isDisabled={businessLocked}
                      onChange={(event) => setBusiness((prev) => ({ ...prev, [key]: event.target.value }))}
                    />
                  </FormControl>
                ))}
              </SimpleGrid>
            </TabPanel>
            <TabPanel>
              <StepNote>
                This is the certificate Revenu Québec has issued for the cloud backend in {envirn}. The private key stays in Firestore. The green count is the number of days left before the expiration date. When fewer than 90 days remain, replace the certificate before it expires. A request that was already used to create this certificate cannot be used again.
              </StepNote>
              <Text fontWeight="600">Active certificate ({envirn})</Text>
              <Text fontSize="sm" color="gray.600" mb={4}>Currently installed server certificate</Text>
              {status.enrolled ? (
                <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
                  <Box
                    gridColumn={{ lg: "span 2" }}
                    borderRadius="2xl"
                    p={6}
                    color="white"
                    bgGradient="linear(to-br, #4f46e5, #3730a3)"
                    minH="240px"
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                  >
                    <HStack justify="space-between" align="flex-start">
                      <Box>
                        <Text fontSize="10px" letterSpacing="0.2em" textTransform="uppercase" opacity={0.7}>
                          Active certificate ({envirn})
                        </Text>
                        <Text fontSize="xl" fontWeight="700" mt={1}>
                          {csrForm.userId || status.cert.nickname || "Certificate"}
                        </Text>
                      </Box>
                      <HStack bg="whiteAlpha.300" border="1px solid" borderColor="whiteAlpha.400" borderRadius="lg" px={3} py={1}>
                        <Box boxSize="6px" borderRadius="full" bg={expiry && expiry.daysLeft < 0 ? "red.300" : "green.300"} />
                        <Text fontSize="10px" fontWeight="700" letterSpacing="0.12em">
                          {expiry && expiry.daysLeft < 0 ? "EXPIRED" : "VALID"}
                        </Text>
                      </HStack>
                    </HStack>
                    <Box>
                      <SimpleGrid columns={2} spacing={6} mb={4}>
                        <Box>
                          <Text fontSize="10px" letterSpacing="0.14em" textTransform="uppercase" opacity={0.7} mb={1}>Serial number</Text>
                          <Text fontFamily="mono" fontSize="xs" fontWeight="700">{status.cert.serial || "—"}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="10px" letterSpacing="0.14em" textTransform="uppercase" opacity={0.7} mb={1}>Organization</Text>
                          <Text fontFamily="mono" fontSize="xs" fontWeight="700">{status.organization || "—"}</Text>
                        </Box>
                      </SimpleGrid>
                      <HStack justify="space-between" align="flex-end" borderTop="1px solid" borderColor="whiteAlpha.300" pt={4}>
                        <Box>
                          <Text fontSize="10px" letterSpacing="0.14em" textTransform="uppercase" opacity={0.7} mb={1}>Expiration date</Text>
                          <Text fontSize="lg" fontWeight="700">{status.cert.expiry || "—"}</Text>
                        </Box>
                        {expiry ? (
                          <Box bg={expiry.warning ? "red.500" : "green.500"} borderRadius="lg" px={3} py={1}>
                            <Text fontSize="10px" fontWeight="700" letterSpacing="0.08em">
                              {expiry.label}
                            </Text>
                          </Box>
                        ) : null}
                      </HStack>
                    </Box>
                  </Box>
                  <Box border="1px solid" borderColor="#d5e4ea" borderRadius="2xl" p={6} bg="gray.50">
                    <HStack justify="space-between" mb={3}>
                      <Text fontSize="10px" fontWeight="700" letterSpacing="0.16em" color="gray.500">CERTIFICATE CONTENT</Text>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          navigator.clipboard.writeText(status.cert.fullText || "");
                          setCopied(true);
                          window.setTimeout(() => setCopied(false), 2000);
                        }}
                      >
                        {copied ? "Copied" : "Copy"}
                      </Button>
                    </HStack>
                    <Textarea value={status.cert.fullText} readOnly minH="140px" fontFamily="mono" fontSize="xs" bg="white" />
                  </Box>
                </SimpleGrid>
              ) : (
                <Box border="2px dashed" borderColor="gray.200" borderRadius="2xl" py={12} textAlign="center">
                  <Text fontWeight="600">No active certificate.</Text>
                  <Text fontSize="sm" color="gray.600" mt={1}>Generate a request, then add the certificate.</Text>
                </Box>
              )}
              {status.enrolled && expiry?.notice ? (
                <Box mt={4} bg="red.50" border="1px solid" borderColor="red.200" borderRadius="md" px={4} py={3}>
                  <Text fontSize="sm" color="red.700">{expiry.notice}</Text>
                </Box>
              ) : null}
            </TabPanel>
            <TabPanel>
              <StepNote>
                {`A certificate request can be sent to Revenu Québec only once.\n\nIf no certificate is active, use the new request to add the certificate.\nIf a certificate is already active, you can replace it, or add another certificate. Add certificate asks you to type add before that request is sent.\nAfter the request is accepted, it is closed. Generate a new request for the next change.\n\nGenerating a request here creates the private key in the cloud. It replaces any unused request for this environment.\n\n${envirn === "PROD"
                  ? "Production starts blank. The DEV and ESSAI test numbers are not used here. Enter the production identification number, authorization code, QST number, GST number, and dossier number, then save."
                  : "DEV and ESSAI show the test numbers for this stage. Change them if needed, then save before you generate a request."}`}
              </StepNote>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {field("Identification number", csrForm.userId, (value) => setCsrForm((prev) => ({ ...prev, userId: value })))}
                {field("Authorization code", csrForm.authCode, (value) => setCsrForm((prev) => ({ ...prev, authCode: value })))}
                {field("QST number", csrForm.qstNumber, (value) => setCsrForm((prev) => ({ ...prev, qstNumber: value })))}
                {field("GST number", csrForm.gstNumber, (value) => setCsrForm((prev) => ({ ...prev, gstNumber: value })))}
                {field("Dossier number", csrForm.dossierNumber, (value) => setCsrForm((prev) => ({ ...prev, dossierNumber: value })))}
                <FormControl>
                  <FormLabel>Timezone</FormLabel>
                  <Select value={csrForm.timezone} onChange={(event) => setCsrForm((prev) => ({ ...prev, timezone: event.target.value }))}>
                    <option value="-05:00">-05:00</option>
                    <option value="-04:00">-04:00</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Activity subsector</FormLabel>
                  <Select value={csrForm.activitySubsector} onChange={(event) => setCsrForm((prev) => ({ ...prev, activitySubsector: event.target.value }))}>
                    <option value="Restaurant">Restaurant</option>
                    <option value="Bar">Bar</option>
                    <option value="Food Truck">Food Truck</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Certificate nickname</FormLabel>
                  <InputGroup>
                    <Input
                      value={nicknameEditing ? nicknameDraft : savedServerNickname(nicknamesByCase, addCase, csrForm.nickname)}
                      isReadOnly={!nicknameEditing}
                      pr="5.5rem"
                      onChange={(event) => setNicknameDraft(event.target.value)}
                    />
                    <InputRightElement width="5.5rem">
                      <Button
                        size="sm"
                        h="1.75rem"
                        variant={nicknameEditing ? "solid" : "outline"}
                        colorScheme={nicknameEditing ? "blue" : undefined}
                        onClick={() => {
                          if (!nicknameEditing) {
                            setNicknameDraft(savedServerNickname(nicknamesByCase, addCase, csrForm.nickname));
                            setNicknameEditing(true);
                            return;
                          }
                          const next = sanitizeCertificateNickname(nicknameDraft);
                          if (!next) return;
                          ask({
                            title: "Save this server certificate name?",
                            message: `This name is saved for test case ${addCase} in ${envirn} only. It is not used for another test case or environment.`,
                            confirmLabel: "Save name",
                            action: () => run("saveNickname", "saveServerCertificateProfile", {
                              certificateNickname: next,
                              caseEssai: addCase,
                            }, "Certificate nickname saved"),
                          });
                        }}
                      >
                        {nicknameEditing ? "Save" : "Edit"}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <Text mt={1} fontSize="sm" color={nicknameEditing && !nicknameDraft.trim() ? "red.600" : "gray.600"}>
                    {nicknameEditing && !nicknameDraft.trim()
                      ? "Enter a server certificate nickname."
                      : savedServerNickname(nicknamesByCase, addCase, csrForm.nickname)
                        ? `Saved for test case ${addCase} in ${envirn} only. Generate CSR uses this name.`
                        : "Enter the certificate nickname for this test case. Generate CSR uses the saved name."}
                  </Text>
                </FormControl>
                {field("Organization", csrForm.authCode ? `FOB-${csrForm.authCode.replace(/^(RBC|FOB)-/i, "")}` : "", () => undefined, true)}
              </SimpleGrid>
              {envirn === "PROD" && (csrChanged || !status.csrFormSaved) ? (
                <Text mt={3} fontSize="sm" color="gray.700">
                  These production details are not saved yet. Save them before you generate the certificate request.
                </Text>
              ) : null}
              <HStack mt={4} spacing={3}>
              <Button variant="outline" isLoading={busy === "saveCsr"} isDisabled={!csrChanged && status.csrFormSaved} onClick={() => ask({
                title: "Save certificate details?",
                message: envirn === "PROD"
                  ? "These production details will be stored in Firestore. They replace any earlier values for this environment. The test numbers from DEV and ESSAI are not saved here."
                  : `These ${envirn} details will be stored in Firestore and used the next time you generate a certificate request.`,
                confirmLabel: "Save details",
                action: () => run("saveCsr", "saveServerCertificateProfile", { csrForm }, "Certificate details saved"),
              })}>
                Save details
              </Button>
              <Button colorScheme="blue" isLoading={busy === "csr"} onClick={() => ask({
                title: "Generate a new certificate request?",
                message: `This creates a new private key in Firestore for ${envirn}. The certificate name is ${sanitizeCertificateNickname(savedServerNickname(nicknamesByCase, addCase, csrForm.nickname))}. Any unused request for this environment is replaced and can no longer be sent.`,
                confirmLabel: "Generate request",
                action: () => {
                  const certificateNickname = sanitizeCertificateNickname(savedServerNickname(nicknamesByCase, addCase, csrForm.nickname));
                  return run("csr", "createServerCertificateCsr", {
                    csrForm: { ...csrForm, nickname: certificateNickname },
                    certificateNickname,
                    caseEssai: addCase,
                  }, "Certificate request created");
                },
              })}>
                Generate CSR
              </Button>
              </HStack>
              <FormControl mt={4}>
                <FormLabel>Generated CSR</FormLabel>
                <Textarea value={status.csrPem} readOnly minH="180px" fontFamily="mono" fontSize="xs" />
              </FormControl>
              {status.csrGeneratedAt ? (
                <Text mt={2} fontSize="sm" color="gray.600">
                  Created {new Date(status.csrGeneratedAt).toLocaleString()}. The subject above is the information stored with this request.
                </Text>
              ) : (
                <Text mt={2} fontSize="sm" color="gray.600">No request yet. Fill in the subject, then generate the request.</Text>
              )}
              {requestUsed ? (
                <Box mt={3} bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" px={4} py={3}>
                  <Text fontSize="sm">
                    This request was used to {usedAction} at {usedWhen}. It cannot be used again. Generate a new request before you add or replace a certificate.
                  </Text>
                </Box>
              ) : status.csrPem ? (
                <Box mt={4}>
                  <Text fontSize="sm" mb={3}>
                    {status.enrolled
                      ? "A certificate is already active. You can replace it, or add another certificate. Add certificate asks you to type add before the request is sent."
                      : "No certificate is active. This unused request can add the server certificate. Replacement stays closed until a certificate is active."}
                  </Text>
                  <HStack spacing={3} wrap="wrap">
                    <Button colorScheme="blue" isDisabled={!canAdd} onClick={() => setTabIndex(3)}>
                      Use for adding certificate
                    </Button>
                    <Button variant="outline" isDisabled={!canReplace} onClick={() => setTabIndex(4)}>
                      Use for replacing certificate
                    </Button>
                  </HStack>
                </Box>
              ) : null}
            </TabPanel>
            <TabPanel>
              <StepNote>
                {status.enrolled
                  ? "A certificate is already active. Add certificate shows a warning. Type add to send this request."
                  : requestUsed
                    ? `This request was used to ${usedAction} at ${usedWhen}. It cannot be sent again. Generate a new request first.`
                    : "This step enrolls the server certificate for the first time. Revenu Québec accepts the request only once. The CASESSAI field is the test case that will be sent, with APPRLINIT SRV and no device ID. The private key already stored in Firestore stays in the cloud."}
              </StepNote>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {field("APPRLINIT", status.apprlinit, () => undefined, true)}
                <CaseEssaiField value={addCase} onChange={setAddCase} readOnly={envirn === "PROD"} onEditingChange={setAddCaseEditing} />
                {field("IDSEV", headers.idsev, (value) => setHeaders((prev) => ({ ...prev, idsev: value })))}
                {field("IDVERSI", headers.idversi, (value) => setHeaders((prev) => ({ ...prev, idversi: value })))}
                {field("CODCERTIF", headers.codcertif, (value) => setHeaders((prev) => ({ ...prev, codcertif: value })))}
                {field("IDPARTN", headers.idpartn, (value) => setHeaders((prev) => ({ ...prev, idpartn: value })))}
                {field("VERSI", headers.versi, (value) => setHeaders((prev) => ({ ...prev, versi: value })))}
                {field("VERSIPARN", headers.versiparn, (value) => setHeaders((prev) => ({ ...prev, versiparn: value })))}
              </SimpleGrid>
              <FormControl mt={4}>
                <FormLabel>Modification</FormLabel>
                <Input value="AJO" isReadOnly />
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Certificate request that will be sent</FormLabel>
                <Textarea value={status.csrPem} readOnly minH="140px" fontFamily="mono" fontSize="xs" />
              </FormControl>
              <Button mt={4} colorScheme="blue" isLoading={busy === "add"} isDisabled={!canAdd || addCaseEditing} onClick={() => ask({
                title: status.enrolled ? "A certificate is already active" : "Add the server certificate?",
                message: status.enrolled
                  ? `A server certificate is already active for ${envirn}. Adding another certificate sends this request to Revenu Québec. Type add to continue.`
                  : `This enrolls the unused request with Revenu Québec for ${envirn}. The request can be sent only once. The private key stays in Firestore.`,
                confirmLabel: "Add certificate",
                typedWord: status.enrolled ? "add" : undefined,
                action: () => run("add", "enrollServerCertificate", { headers, caseEssai: addCase }, "Server certificate enrolled"),
              })}>
                Add certificate
              </Button>
            </TabPanel>
            <TabPanel>
              <StepNote>
                {!status.enrolled
                  ? "Replacement is closed because no certificate is active. Add the certificate first. After it is active, generate a new request and use that new request here."
                  : requestUsed
                    ? `This request was used to ${usedAction} at ${usedWhen}. It cannot replace the certificate. Generate a new request, then return to this step.`
                    : "This step replaces the active certificate. Revenu Québec requires a new request that has not been used. The serial number below is the certificate that is active now. The CASESSAI field is the test case that will be sent. After Revenu Québec accepts it, this request is closed."}
              </StepNote>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {field("APPRLINIT", status.apprlinit, () => undefined, true)}
                <CaseEssaiField value={replaceCase} onChange={setReplaceCase} readOnly={envirn === "PROD"} onEditingChange={setReplaceCaseEditing} />
              </SimpleGrid>
              <FormControl mt={4}>
                <FormLabel>Modification</FormLabel>
                <Input value="REM" isReadOnly />
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Serial number</FormLabel>
                <Input value={serial} onChange={(event) => setSerial(event.target.value)} />
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Certificate request that will be sent</FormLabel>
                <Textarea value={status.csrPem} readOnly minH="140px" fontFamily="mono" fontSize="xs" />
              </FormControl>
              <Button mt={4} colorScheme="blue" isLoading={busy === "replace"} isDisabled={!canReplace || replaceCaseEditing} onClick={() => ask({
                title: "Replace the active certificate?",
                message: `This retires serial ${serial || "the active certificate"} in ${envirn} and asks Revenu Québec to issue a new certificate. The request is closed after it is accepted. Type replace to continue.`,
                confirmLabel: "Replace certificate",
                typedWord: "replace",
                action: () => run("replace", "replaceServerCertificate", { serial, headers, caseEssai: replaceCase }, "Server certificate replaced"),
              })}>
                Replace certificate
              </Button>
            </TabPanel>
            <TabPanel>
              <StepNote>
                This step removes the active server certificate from Revenu Québec. The serial number is taken from the certificate that is active now, not from an older certificate you replaced. The CASESSAI field is the test case that will be sent. Deleting does not create a new request. After the certificate is gone, generate a new request and use it to add the certificate again.
              </StepNote>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {field("APPRLINIT", status.apprlinit, () => undefined, true)}
                <CaseEssaiField value={deleteCase} onChange={setDeleteCase} readOnly={envirn === "PROD"} onEditingChange={setDeleteCaseEditing} />
              </SimpleGrid>
              <FormControl mt={4}>
                <FormLabel>Modification</FormLabel>
                <Input value="SUP" isReadOnly />
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Serial number</FormLabel>
                <Input value={serial} onChange={(event) => setSerial(event.target.value)} />
              </FormControl>
              <Button mt={4} colorScheme="red" variant="outline" isLoading={busy === "delete"} isDisabled={!status.enrolled || deleteCaseEditing} onClick={() => ask({
                title: "Delete the active certificate?",
                message: `This removes serial ${serial || "the active certificate"} from Revenu Québec for ${envirn}. The cloud backend cannot use that certificate after it is deleted. Type delete to continue.`,
                confirmLabel: "Delete certificate",
                typedWord: "delete",
                action: () => run("delete", "deleteServerCertificate", { serial, headers, caseEssai: deleteCase }, "Server certificate deleted"),
              })}>
                Delete certificate
              </Button>
            </TabPanel>
          </TabPanels>
        </Tabs>
        <Box mt={6}>
          <Text fontSize="sm" fontWeight="600" mb={2}>Server response</Text>
          <Text fontSize="sm" color="gray.600" mb={3}>
            Each block is the reply stored in Firestore for this environment. A reply does not reopen a certificate request. If a step fails, read the reply, correct the step, and send a new request when the previous one was already used.
          </Text>
          {status.logs.length === 0 ? (
            <Text fontSize="sm" color="gray.600">No response yet.</Text>
          ) : (
            <VStack align="stretch" spacing={3}>
              {status.logs.map((item) => (
                <Box key={item.id} border="1px solid" borderColor={item.isError ? "red.200" : "#d5e4ea"} borderRadius="md" p={3}>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="xs" color="gray.600">{[item.at, item.label].filter(Boolean).join(" · ")}</Text>
                    <Button size="xs" variant="ghost" onClick={() => ask({
                      title: "Remove this reply?",
                      message: "This removes the stored Revenu Québec reply from Firestore. It does not change the certificate.",
                      confirmLabel: "Remove reply",
                      action: () => run("log", "saveServerCertificateProfile", { removeLogId: item.id }, "Response removed"),
                    })}>
                      Delete
                    </Button>
                  </HStack>
                  <Textarea value={item.text} readOnly minH="80px" fontFamily="mono" fontSize="xs" />
                </Box>
              ))}
            </VStack>
          )}
        </Box>
      </Box>
      <AlertDialog isOpen={Boolean(pendingConfirm)} leastDestructiveRef={cancelRef} onClose={() => setPendingConfirm(null)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>{pendingConfirm?.title}</AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={pendingConfirm?.typedWord ? 4 : 0}>{pendingConfirm?.message}</Text>
              {pendingConfirm?.typedWord ? (
                <FormControl>
                  <FormLabel>Type {pendingConfirm.typedWord}</FormLabel>
                  <Input
                    value={typedConfirm}
                    autoComplete="off"
                    autoFocus
                    onChange={(event) => setTypedConfirm(event.target.value)}
                  />
                </FormControl>
              ) : null}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setPendingConfirm(null)}>Cancel</Button>
              <Button
                ml={3}
                colorScheme={pendingConfirm?.typedWord === "delete" ? "red" : "blue"}
                isDisabled={!typedWordMatches}
                onClick={() => {
                  const action = pendingConfirm?.action;
                  setPendingConfirm(null);
                  setTypedConfirm("");
                  void action?.();
                }}
              >
                {pendingConfirm?.confirmLabel}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </PageShell>
  );
};

export default ServerCertificatePage;
